import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LogOut, Bell, BellOff, BarChart3 } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { subscribeToPush, unsubscribeFromPush, getPushSubscription } from "../lib/push";
import { isNative, registerNativePush, unregisterNativePush } from "../lib/native";

import ProfileCard from "../components/settings/ProfileCard";
import CoachPrefsCard from "../components/settings/CoachPrefsCard";
import AppearanceCard from "../components/settings/AppearanceCard";
import DataCard from "../components/settings/DataCard";
import AboutCard from "../components/settings/AboutCard";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function Settings() {
    const { logout } = useAuth();
    const [pushOn, setPushOn] = useState(false);
    const [hour, setHour] = useState(() => {
        const stored = parseInt(localStorage.getItem("vf_reminder_hour") || "", 10);
        return Number.isFinite(stored) ? stored : 9;
    });
    const [busy, setBusy] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        // Silently probe admin status — 404 for non-admins, so we swallow errors.
        api.get("/admin/me").then(({ data }) => setIsAdmin(!!data.is_admin)).catch(() => {});
    }, []);

    useEffect(() => {
        if (isNative()) {
            setPushOn(localStorage.getItem("vf_native_push") === "1");
        } else {
            getPushSubscription().then((s) => setPushOn(!!s));
        }
    }, []);

    const togglePush = async (next) => {
        setBusy(true);
        try {
            if (next) {
                const utcHour = (hour - new Date().getTimezoneOffset() / 60 + 24) % 24;
                if (isNative()) {
                    await registerNativePush();
                    localStorage.setItem("vf_native_push", "1");
                    localStorage.setItem("vf_reminder_hour", String(hour));
                } else {
                    await subscribeToPush({ reminderHourUtc: Math.floor(utcHour) });
                }
                setPushOn(true);
                toast.success("Daily reminder set");
            } else {
                if (isNative()) {
                    await unregisterNativePush();
                    localStorage.removeItem("vf_native_push");
                } else {
                    await unsubscribeFromPush();
                }
                setPushOn(false);
                toast.success("Reminders off");
            }
        } catch (e) {
            toast.error(e?.message || "Couldn't update reminders");
        } finally {
            setBusy(false);
        }
    };

    const updateHour = async (h) => {
        setHour(h);
        localStorage.setItem("vf_reminder_hour", String(h));
        if (pushOn && !isNative()) {
            try {
                const utcHour = (h - new Date().getTimezoneOffset() / 60 + 24) % 24;
                await subscribeToPush({ reminderHourUtc: Math.floor(utcHour) });
                toast.success("Reminder time updated");
            } catch {
                toast.error("Couldn't update time");
            }
        }
    };

    return (
        <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-8 space-y-4" data-testid="settings-page">
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400">Your studio</div>
                <h1 className="font-serif text-4xl sm:text-5xl tracking-tight font-light mt-2 mb-2">Settings</h1>
            </motion.div>

            <ProfileCard />
            <CoachPrefsCard />

            {/* Reminders */}
            <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6" data-testid="settings-reminders">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">Daily reminder</div>
                        <div className="font-medium mt-1 flex items-center gap-2">
                            {pushOn ? <Bell size={14} strokeWidth={1.5} className="text-terracotta" /> : <BellOff size={14} strokeWidth={1.5} className="text-stone-400" />}
                            Browser notification
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5">A gentle nudge with today&apos;s prompt — never spammy.</div>
                    </div>
                    <Switch
                        checked={pushOn}
                        onCheckedChange={togglePush}
                        disabled={busy}
                        data-testid="reminder-toggle"
                    />
                </div>
                {pushOn && (
                    <div className="mt-4 flex items-center gap-3">
                        <span className="text-xs text-stone-500">Send at</span>
                        <select
                            value={hour}
                            onChange={(e) => updateHour(Number(e.target.value))}
                            className="rounded-full px-3 h-9 border border-stone-200 dark:border-stone-700 bg-white dark:bg-[#1f1e1d] text-sm"
                            data-testid="reminder-hour"
                        >
                            {HOURS.map((h) => (
                                <option key={h} value={h}>
                                    {(h % 12 === 0 ? 12 : h % 12)}:00 {h < 12 ? "AM" : "PM"} (your time)
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <AppearanceCard />
            <DataCard />
            <AboutCard />

            {isAdmin && (
                <div className="rounded-3xl border border-terracotta/30 bg-terracotta/[0.04] p-5" data-testid="settings-admin-link">
                    <Link
                        to="/admin"
                        className="flex items-center justify-between gap-3 group"
                    >
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-terracotta text-white">
                                <BarChart3 size={17} strokeWidth={1.7} />
                            </span>
                            <div>
                                <div className="font-medium text-[#1A1A1A] dark:text-[#F6F5F2]">Admin dashboard</div>
                                <div className="text-xs text-stone-500">Real numbers from your MongoDB</div>
                            </div>
                        </div>
                        <span className="text-terracotta text-sm group-hover:translate-x-0.5 transition-transform">Open →</span>
                    </Link>
                </div>
            )}

            <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6">
                <Button
                    variant="ghost"
                    onClick={logout}
                    className="w-full justify-start rounded-2xl text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                    data-testid="settings-logout"
                >
                    <LogOut size={16} className="mr-2" /> Sign out
                </Button>
            </div>

            <div className="text-center text-xs text-stone-400 font-serif italic pt-4">
                Cadence — speak with rhythm.
            </div>
        </div>
    );
}
